import { useEffect, useState } from 'react';

/**
 * Full view hides the editor header (title, actions, menus) so the grid gets
 * its height back; the toolbar and formula bar stay. The state lives on a
 * body class so CSS can hide the header, is remembered per browser, and is
 * announced with a resize so the sheet re-measures its view height.
 */
export const FULL_VIEW_CLASS = 'grid-full-view';
export const FULL_VIEW_STORAGE_KEY = 'milegrid-full-view';
const CHANGE_EVENT = 'milegrid-full-view-change';

/** The editor header's designed height; used until the header is mounted. */
const HEADER_HEIGHT = 56;

export const isFullView = () => document.body.classList.contains(FULL_VIEW_CLASS);

export const setFullView = (on) => {
  document.body.classList.toggle(FULL_VIEW_CLASS, !!on);
  try {
    localStorage.setItem(FULL_VIEW_STORAGE_KEY, on ? '1' : '0');
  } catch (e) {
    // Private mode or blocked storage: the choice just is not remembered.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
  window.dispatchEvent(new Event('resize'));
};

export const toggleFullView = () => setFullView(!isFullView());

/** Applies the remembered state; call once before the sheet first measures. */
export const restoreFullView = () => {
  let remembered = null;
  try {
    remembered = localStorage.getItem(FULL_VIEW_STORAGE_KEY);
  } catch (e) {
    remembered = null;
  }
  if (remembered === '1' && !isFullView()) {
    document.body.classList.add(FULL_VIEW_CLASS);
  }
};

/**
 * Height of the editor header as currently laid out (0 while full view hides
 * it), so the sheet's view height follows whatever the header is doing.
 */
export const headerHeight = () => {
  const header = document.getElementById('myy-header');
  return header ? header.offsetHeight : HEADER_HEIGHT;
};

/** Ctrl+Shift+F (Cmd+Shift+F on Mac) toggles full view, as in Sheets. */
export const useFullViewShortcut = () => {
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && !e.altKey && (e.key === 'F' || e.key === 'f')) {
        e.preventDefault();
        toggleFullView();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
};

/** React binding: [isOn, toggle], re-rendering when it changes anywhere. */
export const useFullView = () => {
  const [on, setOn] = useState(() => {
    restoreFullView();
    return isFullView();
  });
  useEffect(() => {
    const sync = () => setOn(isFullView());
    window.addEventListener(CHANGE_EVENT, sync);
    return () => window.removeEventListener(CHANGE_EVENT, sync);
  }, []);
  return [on, toggleFullView];
};
